'use client';

import Image from 'next/image';
import { motion } from 'motion/react';
import { CircleDot } from 'lucide-react';
import { ICONS } from '@/lib/iconRegistry';
import GlobalTitle from '@shared/GlobalTitle';
import CredentialsMarquee from '@shared/CredentialsMarquee';

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};
const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.14 } },
};

export default function WhyChooseServices({ data }) {
    if (!data) return null;

    return (
        <section className="relative overflow-hidden min-h-[600px] flex items-stretch">
            <div className="absolute inset-0">
                {data.image ? (
                    <Image
                        src={data.image}
                        alt={data.imageAlt}
                        fill
                        sizes="100vw"
                        quality={80}
                        draggable="false"
                        className="object-cover object-center"
                        priority
                    />
                ) : (
                    // Fallback background when no image is provided
                    <div className="absolute inset-0 bg-linear-to-br from-gray-950 via-slate-900 to-gray-950">
                        <div className="absolute -top-32 -left-32 w-130 h-130 rounded-full bg-primary/25 blur-[120px]" />
                        <div className="absolute -bottom-40 right-0 w-150 h-150 rounded-full bg-primary/15 blur-[140px]" />
                        <div className="absolute inset-0 opacity-[0.06] bg-[linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] bg-size-[48px_48px]" />
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-gray-950/90 via-gray-950/70 to-gray-950/70" />
            </div>

            <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="block lg:sticky lg:self-center">
                    <motion.div variants={fadeUp}>
                        <GlobalTitle
                            topText={data.topText}
                            titleText={data.titleText}
                            coloredText={data.coloredText}
                            classes="text-left text-white !mb-4"
                        />
                    </motion.div>
                    {data.leftDescription && (
                        Array.isArray(data.leftDescription) ? (
                            <motion.div variants={fadeUp} className="space-y-3 mb-6">
                                {data.leftDescription.map((line, i) => (
                                    <div key={i} className="flex gap-3 items-start">
                                        <span className="relative top-1 font-mono text-[10.5px] tracking-[0.14em] text-primary">
                                            <CircleDot size={15} />
                                        </span>
                                        <p className="text-white/85 text-md leading-relaxed">{line}</p>
                                    </div>
                                ))}
                            </motion.div>
                        ) : (
                            <motion.p variants={fadeUp} className="text-white text-md leading-relaxed mb-6">
                                {data.leftDescription}
                            </motion.p>
                        )
                    )}
                    <motion.div variants={fadeUp} className={`${data.leftDescription ? "pt-0" : "pt-10"} space-y-3`}>
                        <p className="text-sm font-semibold text-muted tracking-[0.15em] mb-0 uppercase">Our Credentials</p>
                        <CredentialsMarquee className="bg-transparent" showMask={false} cardHeight="h-[80px]" />
                    </motion.div>
                </div>
                <motion.div
                    variants={stagger}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    className="rounded-3xl p-0 sm:p-8 backdrop-blur-md bg-gray-950/70 border-0 sm:border sm:border-white/10 shadow-2xl"
                >
                    {data.description && (
                        <>
                            <motion.p variants={fadeUp} className="text-white/80 text-md leading-relaxed mb-6">
                                {data.description}
                            </motion.p>
                            <motion.div variants={fadeUp} className="h-px bg-white/10 mb-6" />
                        </>
                    )}

                    <div className="space-y-4">
                        {data.stats?.map((stat, i) => {
                            const Icon = ICONS[stat.icon];
                            return (
                                <motion.div
                                    key={i}
                                    variants={fadeUp}
                                    className="group flex gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-primary/40 transition-all duration-300"
                                >
                                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-primary/20 flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors duration-300 mt-0.5">
                                        {Icon && (
                                            <Icon
                                                size={16}
                                                className="text-primary group-hover:text-white transition-colors duration-300 sm:w-[18px] sm:h-[18px]"
                                            />
                                        )}
                                    </div>

                                    <div className="min-w-0">
                                        <p className="font-bold text-white text-sm sm:text-md leading-snug mb-1">
                                            {stat.value}
                                        </p>
                                        <p className="text-white/80 text-xs sm:text-sm leading-relaxed">
                                            {stat.label}
                                        </p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
